import { createRenderActiveShape } from 'pages/Dashboard/components/pie_chart_util';
import {
    SegmentConfig,
    StackedHorizontalBarChart,
} from 'pages/Dashboard/components/StackedHorizontalBarChart';
import {
    CenteredChartContainer,
    PieChartLegendContainer,
    PieChartLegendItem,
    StyledPieChart,
} from 'pages/Dashboard/components/styled';
import { VerticalScrollableBarChart } from 'pages/Dashboard/components/VerticalScrollableBarChart';
import { getIssueSeverity } from 'pages/Desk/issues/shared';
import { useMemo } from 'react';
import { Cell, Pie } from 'recharts';
import styled from 'styled-components';
import { tokens } from 'styles/tokens';
import { sortBy } from 'utils/array';
import { round } from 'utils/number';

type AssetSeverities = {
    AssetTypeId: number;
    AssetTypeName: string;
    LowCount: number;
    ModerateCount: number;
    SevereCount: number;
};

/** used for "overall severity" counts */
type SeverityCounts = {
    LowCount: number;
    ModerateCount: number;
    SevereCount: number;
};

const Container = styled.div`
    display: grid;
    grid-template-areas:
        'bars pie'
        'bars pie';
    grid-template-columns: 1fr auto;
    grid-template-rows: 1fr auto;
    gap: 1rem;
    margin-right: 1rem;
    height: 100%;
    min-height: 30rem;

    position: relative;
    .pie {
        padding-left: 1rem;
        margin-left: 0.2rem;
        height: fit-content;
        & > *:nth-child(2) {
            border-top: 1px dashed ${tokens.nav.border};
            border-bottom: 1px dashed ${tokens.nav.border};
        }
    }
`;

const BarsContainer = styled.div`
    grid-area: bars;
    min-height: 20rem;
`;

const PieContainer = styled(CenteredChartContainer)`
    grid-area: pie;
`;

const LegendContainer = styled(PieChartLegendContainer)`
    grid-area: legend;
    gap: 0.5rem;
`;

const SEVERITY = {
    low: getIssueSeverity(0),
    moderate: getIssueSeverity(0.5),
    severe: getIssueSeverity(1),
};

type PieChartData = {
    name: string;
    value: number;
    fractionOfTotal: number;
    fill: string;
    textColor?: string;
};

function getSeverityBarConfigsLowToHigh(
    overallSeverity: SeverityCounts | undefined,
): PieChartData[] {
    if (!overallSeverity) {
        return [];
    }
    const totalCount =
        overallSeverity.LowCount +
        overallSeverity.ModerateCount +
        overallSeverity.SevereCount;

    return [
        {
            name: 'Low',
            fill: SEVERITY.low.backgroundColor,
            value: overallSeverity.LowCount,
            fractionOfTotal: overallSeverity.LowCount / totalCount,
        },
        {
            name: 'Moderate',
            fill: SEVERITY.moderate.backgroundColor,
            value: overallSeverity.ModerateCount,
            fractionOfTotal: overallSeverity.ModerateCount / totalCount,
        },
        {
            name: 'Severe',
            fill: SEVERITY.severe.backgroundColor,
            value: overallSeverity.SevereCount,
            fractionOfTotal: overallSeverity.SevereCount / totalCount,
        },
    ];
}

type BarChartDataRow = {
    name: string;
    Low: number;
    Moderate: number;
    Severe: number;
};

export function AssetFaultsBySeverity({
    assetData,
    overallSeverity,
}: {
    assetData: AssetSeverities[];
    overallSeverity: SeverityCounts | undefined;
}) {
    const barChartData = useMemo(
        () =>
            sortBy(
                assetData,
                { key: (a) => a.LowCount + a.ModerateCount + a.SevereCount, dir: 'desc' },
                { key: (a) => a.SevereCount, dir: 'desc' },
                { key: (a) => a.ModerateCount, dir: 'desc' },
                { key: (a) => a.LowCount, dir: 'desc' },
            )
                .slice(0, Math.min(20, assetData.length))
                .map((asset) => ({
                    name: asset.AssetTypeName,
                    Low: asset.LowCount,
                    Moderate: asset.ModerateCount,
                    Severe: asset.SevereCount,
                })),
        [assetData],
    );

    const severityInfoLowToHigh = useMemo(
        () => getSeverityBarConfigsLowToHigh(overallSeverity),
        [overallSeverity],
    );

    const severityInfoHighToLow = severityInfoLowToHigh.toReversed();

    const barSegments: SegmentConfig<BarChartDataRow>[] = useMemo(
        () =>
            severityInfoHighToLow.map((severity) => ({
                dataKey: severity.name as keyof BarChartDataRow,
                label: severity.name,
                color: severity.fill,
                textColor: severity.textColor,
                stackId: 'severity',
            })),
        [severityInfoHighToLow],
    );

    return (
        <Container>
            <BarsContainer>
                <VerticalScrollableBarChart
                    xAxisHeight={30}
                    dataLength={barChartData.length}
                    minBarHeight={42}
                >
                    {({ xAxisHeight }) => (
                        <StackedHorizontalBarChart
                            data={barChartData}
                            segments={barSegments}
                            categoryKey="name"
                            yAxisWidth={90}
                            xAxisHeight={xAxisHeight}
                            barCategoryGap={4}
                            showGrid={true}
                            showTooltip={true}
                            showBarLabels={true}
                            stackGap={1}
                            rounding="last-item"
                            cornerRadius={5}
                            labelMinValue="3%"
                        />
                    )}
                </VerticalScrollableBarChart>
            </BarsContainer>
            {severityInfoLowToHigh.length > 0 && (
                <div className="pie">
                    <LegendContainer>
                        <h3
                            style={{
                                margin: 0,
                                fontSize: '1.4rem',
                                color: tokens.text.primary,
                            }}
                        >
                            Overall Severity
                        </h3>
                    </LegendContainer>
                    <PieContainer>
                        <StyledPieChart width={200} height={200}>
                            <Pie
                                data={severityInfoLowToHigh}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                outerRadius={80}
                                innerRadius={50}
                                labelLine={false}
                                activeShape={createRenderActiveShape(
                                    tokens.text.primary,
                                    tokens.text.secondary,
                                    (slice) => `${slice.name}`,
                                    (slice) =>
                                        `${slice.value} (${(
                                            (slice.percent ?? 0) * 100
                                        ).toFixed(0)}%)`,
                                )}
                            >
                                {severityInfoLowToHigh.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={entry.fill} />
                                ))}
                            </Pie>
                        </StyledPieChart>
                    </PieContainer>

                    <LegendContainer>
                        {severityInfoLowToHigh.map((item) => (
                            <PieChartLegendItem
                                key={item.name}
                                $color={item.fill}
                                $variant="square"
                                $gap="0.5ch"
                            >
                                {item.name}: {round(item.fractionOfTotal * 100, 0)}%
                            </PieChartLegendItem>
                        ))}
                    </LegendContainer>
                </div>
            )}
        </Container>
    );
}
